import React, { useEffect, useRef, useState } from 'react';
import { readClipboardImage, CLIPBOARD_MESSAGES } from '../utils/clipboardImage';
import ImageAnnotateModal from './ImageAnnotateModal';

// Local-only sibling of ImageUploadBox: the same click / drag-drop / paste
// affordances, but it hands the picked File to the caller instead of POSTing
// anywhere — for forms that attach the image to their OWN submit request
// (multipart), so nothing lands on the server when the form is cancelled.
// Compression stays the caller's job (e.g. compressAndSet), keeping one
// compression call site per form.
// showPreview defaults ON (operator 2026-09-02: the expense proof picker
// showed only a filename — every form should SEE what it attached).
const LocalImageDropBox = ({ onFile, selectedFile, helperText, showPreview = true, disabled, onClear }) => {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Optional thumbnail of the picked file (screenshot flows want to SEE what
  // they attached). Object URL is owned here: revoked on change/unmount.
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!showPreview || !selectedFile) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [showPreview, selectedFile]);

  const pick = (file) => {
    if (disabled || !file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setError('Please choose an image file');
      return;
    }
    setError(null);
    onFile(file);
  };

  // "Paste" button — tap equivalent of Ctrl/Cmd+V for phones. The clipboard
  // must be read synchronously inside the click (no awaits before it), and the
  // click must not bubble to the box, whose onClick opens the file picker.
  const [pasting, setPasting] = useState(false);
  // Built-in editor (2026-10-20): draw on the picked file; the marked copy
  // replaces it through the same onFile the picker uses.
  const [drawing, setDrawing] = useState(false);
  const handleDrawClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !selectedFile) return;
    setDrawing(true);
  };
  const handlePasteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || pasting) return;
    setError(null);
    setPasting(true);
    readClipboardImage().then(({ file, error: clipErr }) => {
      setPasting(false);
      if (clipErr) { setError(CLIPBOARD_MESSAGES[clipErr] || CLIPBOARD_MESSAGES.failed); return; }
      pick(file);
    });
  };
  const handleSelectClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) inputRef.current?.click();
  };
  const actionRow = (
    <div className="upload-box-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button type="button" className="btn btn-outline btn-small" onClick={handleSelectClick} disabled={disabled}>
        Select file
      </button>
      <button type="button" className="btn btn-outline btn-small" onClick={handlePasteClick} disabled={disabled || pasting} title="Paste the image you copied">
        {pasting ? 'Reading clipboard…' : '📋 Paste'}
      </button>
      {selectedFile && (
        <button type="button" className="btn btn-outline btn-small" onClick={handleDrawClick} disabled={disabled} title="Draw on this image (circle, arrow, note) before it is sent">
          ✏️ Draw
        </button>
      )}
    </div>
  );

  return (
    <div>
      <ImageAnnotateModal
        isOpen={drawing}
        source={selectedFile || null}
        fileName={selectedFile ? selectedFile.name : 'image'}
        onCancel={() => setDrawing(false)}
        onSave={(file) => { setDrawing(false); pick(file); }}
      />
      <div
        className={dragging ? 'image-upload-box dragging' : 'image-upload-box'}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
        onPaste={(e) => {
          const items = e.clipboardData.items;
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
              // Consume the paste: some host modals ALSO listen for paste at
              // the overlay level and route it elsewhere (e.g. the platform
              // create form fills sell-receipt slots) — a paste landing on a
              // focused box must go to this box exactly once.
              e.preventDefault();
              e.stopPropagation();
              pick(items[i].getAsFile());
              break;
            }
          }
        }}
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click(); } }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        style={{
          border: `2px dashed ${dragging ? '#4caf50' : selectedFile ? '#1a7f37' : '#ccc'}`,
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: dragging ? '#e8f5e9' : '#f9f9f9',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease',
          outline: 'none',
        }}
      >
        <input
          type="file"
          ref={inputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }}
        />
        {selectedFile ? (
          <div style={{ color: '#1a7f37', fontSize: '0.9rem' }}>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Selected"
                style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px', display: 'block', margin: '0 auto 8px' }}
              />
            )}
            ✓ Selected: <strong>{selectedFile.name}</strong>
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
              Choose a file, paste, or drop to replace
            </div>
            {actionRow}
            {onClear && !disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClear(); }}
                style={{ marginTop: '6px', background: 'none', border: '1px solid #d32f2f', color: '#d32f2f', borderRadius: '4px', padding: '2px 10px', fontSize: '0.8rem', cursor: 'pointer' }}
              >
                ✕ Remove
              </button>
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '22px', marginBottom: '6px', color: '#aaa' }}>📷</div>
            <div style={{ color: '#555', fontWeight: 500, fontSize: '0.9rem' }}>Choose a file, paste, or drop image here</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>JPG, PNG, WebP</div>
            {actionRow}
          </div>
        )}
      </div>
      {error && <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '5px' }}>{error}</div>}
      {helperText && <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '5px' }}>{helperText}</div>}
    </div>
  );
};

export default LocalImageDropBox;
